// src/modules/app-views/invoices/pages/Document/tabs/RecordTab/index.jsx
// *******************************************************************************
// INVOICE RECORD TAB
// *******************************************************************************
// Main tab for invoice creation. Six Collapse sections:
// 1. Config - Document type and fiscal configuration
// 2. Recipient - Customer selection
// 3. Items - Product list
// 4. Carrier - Carrier entity + freight (NFe only)
// 5. Totals - Invoice totals summary
// 6. Payments - Payment methods
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useEffect, useCallback } from 'react';
import { useDocumentContext } from 'components/core/DocumentProvider';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import { Collapse, Button, Space, Tooltip, Form, Tag } from 'antd';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import ConfigCard from './ConfigCard';
import RecipientCard from './RecipientCard';
import ItemsCard from './ItemsCard';
import CarrierCard from './CarrierCard';
import TotalsCard from './TotalsCard';
import PaymentsCard from './PaymentsCard';
import ValidationAlert from './ValidationAlert';
import translateX from 'utils/translateX';
import useZustand from 'hooks/useZustand';
import {
  SettingFilled,
  UserOutlined,
  EditOutlined,
  PlusOutlined,
  ShoppingCartOutlined,
  CarOutlined,
  DollarOutlined,
  CreditCardOutlined,
} from '@ant-design/icons';
import { isLockedStatus, INVOICE_PURPOSE } from 'services/fiscalService';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const RESOURCE_NFE = 'moduleresources_nfe';
const RESOURCE_NFCE = 'moduleresources_nfce';

const COLLAPSE_STYLES = {
  header: { alignItems: 'center' },
  body: { padding: 20 },
};

const COLLAPSE_STYLE = { marginBottom: 12 };

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const InvoicesRecordTab = ({
  items,
  setItems,
  payments,
  setPayments,
  storedCompany,
  viewMode,
  issuer,
}) => {
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  const { data, loading } = useDocumentContext();
  const { form } = useSimpleFormContext();
  const storedSubscription = useZustand('auth', 'subscription');

  // 2. HOOKS & REFS
  const formDocumentType = Form.useWatch('documentType', form);

  // 3. LOCAL VARIABLES I
  const isNew = !data?._id || data?._id === 'add';
  const subscriptionResources = storedSubscription?.resources || [];
  const hasNfeResource = subscriptionResources.includes(RESOURCE_NFE);
  const hasNfceResource = subscriptionResources.includes(RESOURCE_NFCE);
  const effectiveDocumentType = isNew
    ? formDocumentType || (hasNfceResource && !hasNfeResource ? 'nfce' : 'nfe')
    : data?.documentType || 'nfe';
  const isNfce = effectiveDocumentType === 'nfce';
  const isLocked = viewMode === 'view' || isLockedStatus(data?.status);
  const isAuthorized = data?.status === 'authorized';
  const sefazModelLabel = isNfce ? 'Sefaz Modelo 65' : 'Sefaz Modelo 55';
  const isNoPaymentPurpose =
    data?.purpose === INVOICE_PURPOSE.COMPLEMENTARY ||
    data?.purpose === INVOICE_PURPOSE.RETURN ||
    data?.purpose === INVOICE_PURPOSE.ADJUSTMENT;

  // 4. LOCAL STATE
  const [recipientCollapseKeys, setRecipientCollapseKeys] = useState([]);
  const [collapseInitialized, setCollapseInitialized] = useState(false);
  const [recipientActions, setRecipientActions] = useState(null);
  const [itemsActions, setItemsActions] = useState(null);
  const [carrierActions, setCarrierActions] = useState(null);
  const [paymentsActions, setPaymentsActions] = useState(null);

  // 5. EFFECTS
  useEffect(() => {
    if (collapseInitialized) return;
    if (!data?._id) return;

    setCollapseInitialized(true);

    if (!isLockedStatus(data?.status)) {
      setRecipientCollapseKeys(['recipientInfo']);
    }
  }, [data?._id, data?.status, collapseInitialized]);

  // 6. HANDLERS
  const handleAddItem = newItem => {
    setItems(prev => [...prev, { ...newItem, itemNumber: prev.length + 1 }]);
  };

  const handleUpdateItem = (index, updatedItem) => {
    setItems(prev => prev.map((item, i) => (i === index ? { ...item, ...updatedItem } : item)));
  };

  const handleRemoveItem = index => {
    setItems(prev =>
      prev
        .filter((_, i) => i !== index)
        .map((item, i) => ({ ...item, itemNumber: i + 1 }))
    );
  };

  const handleAddPayment = newPayment => {
    setPayments(prev => [...prev, newPayment]);
  };

  const handleRemovePayment = index => {
    setPayments(prev => prev.filter((_, i) => i !== index));
  };

  const handleRecipientActionsChange = useCallback(actions => {
    setRecipientActions(actions);
  }, []);

  const handleItemsActionsChange = useCallback(actions => {
    setItemsActions(actions);
  }, []);

  const handleCarrierActionsChange = useCallback(actions => {
    setCarrierActions(actions);
  }, []);

  const handlePaymentsActionsChange = useCallback(actions => {
    setPaymentsActions(actions);
  }, []);

  // 7. LOCAL VARIABLES II
  const recipientHeaderExtra =
    !isLocked && !isAuthorized && recipientActions ? (
      <Space size={4} onClick={e => e.stopPropagation()}>
        <Tooltip title={translateX('new_recipient')} placement="top">
          <Button size="small" icon={<PlusOutlined />} onClick={recipientActions.handleNewRecipient} />
        </Tooltip>
        <Tooltip title={translateX('edit_recipient')} placement="top">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={recipientActions.handleEditRecipient}
            disabled={!recipientActions.selectedRecipient || recipientActions.isSystemGenericEntity}
          />
        </Tooltip>
      </Space>
    ) : null;

  const itemsHeaderExtra =
    !isLocked && !isAuthorized && itemsActions ? (
      <Space size={4} onClick={e => e.stopPropagation()}>
        <Tooltip title={translateX('new_item')} placement="top">
          <Button size="small" icon={<PlusOutlined />} onClick={itemsActions.handleAddClick} />
        </Tooltip>
      </Space>
    ) : null;

  const carrierHeaderExtra =
    !isLocked && !isAuthorized && carrierActions ? (
      <Space size={4} onClick={e => e.stopPropagation()}>
        <Tooltip title={translateX('new_carrier')} placement="top">
          <Button size="small" icon={<PlusOutlined />} onClick={carrierActions.handleNewCarrier} />
        </Tooltip>
        <Tooltip title={translateX('edit_carrier')} placement="top">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={carrierActions.handleEditCarrier}
            disabled={!carrierActions.selectedCarrier}
          />
        </Tooltip>
      </Space>
    ) : null;

  const paymentsHeaderExtra =
    !isLocked && !isAuthorized && !isNoPaymentPurpose && paymentsActions ? (
      <Space size={4} onClick={e => e.stopPropagation()}>
        <Tooltip title={translateX('add_payment')} placement="top">
          <Button size="small" icon={<PlusOutlined />} onClick={paymentsActions.handleAddPaymentClick} />
        </Tooltip>
      </Space>
    ) : null;

  // 8. UI
  return (
    <div className="container" style={{ position: 'relative', minHeight: 120 }}>
      <SimpleLoadingArea name="invoiceRecord" isFetching={loading} data={data} />
      <ValidationAlert data={data} />

      {/* Collapse 1: Configuration */}
      <Collapse
        size="small"
        style={COLLAPSE_STYLE}
        styles={COLLAPSE_STYLES}
        items={[
          {
            key: 'invoiceConfiguration',
            forceRender: true,
            label: buildCollapseLabel(<SettingFilled />, translateX('invoice_configuration')),
            extra: <span style={{ fontSize: 12, color: 'inherit', opacity: 0.45 }}>{sefazModelLabel}</span>,
            children: <ConfigCard data={data} viewMode={viewMode} issuer={issuer} />,
          },
        ]}
      />

      {/* Collapse 2: Recipient */}
      <Collapse
        size="small"
        activeKey={recipientCollapseKeys}
        onChange={keys => setRecipientCollapseKeys(keys)}
        style={COLLAPSE_STYLE}
        styles={COLLAPSE_STYLES}
        items={[
          {
            key: 'recipientInfo',
            forceRender: true,
            label: buildCollapseLabel(<UserOutlined />, translateX('recipient')),
            extra: recipientHeaderExtra,
            children: (
              <RecipientCard
                data={data}
                viewMode={viewMode}
                onActionsChange={handleRecipientActionsChange}
              />
            ),
          },
        ]}
      />

      {/* Collapse 3: Items */}
      <Collapse
        size="small"
        style={COLLAPSE_STYLE}
        styles={COLLAPSE_STYLES}
        items={[
          {
            key: 'invoiceItems',
            forceRender: true,
            label: buildCollapseLabel(
              <ShoppingCartOutlined />,
              translateX('invoice_items'),
              items.length > 0 ? (
                <Tag color="blue" style={{ marginLeft: 8 }}>
                  {items.length} {items.length === 1 ? translateX('item') : translateX('items')}
                </Tag>
              ) : null
            ),
            extra: itemsHeaderExtra,
            children: (
              <ItemsCard
                items={items}
                onAddItem={handleAddItem}
                onUpdateItem={handleUpdateItem}
                onRemoveItem={handleRemoveItem}
                onActionsChange={handleItemsActionsChange}
                storedCompany={storedCompany}
                issuer={issuer}
                viewMode={viewMode}
                data={data}
              />
            ),
          },
        ]}
      />

      {/* Collapse 4: Carrier (NFe only) */}
      {!isNfce && (
        <Collapse
          size="small"
          style={COLLAPSE_STYLE}
          styles={COLLAPSE_STYLES}
          items={[
            {
              key: 'carrier',
              forceRender: true,
              label: buildCollapseLabel(<CarOutlined />, translateX('carrier')),
              extra: carrierHeaderExtra,
              children: (
                <CarrierCard
                  data={data}
                  viewMode={viewMode}
                  onActionsChange={handleCarrierActionsChange}
                />
              ),
            },
          ]}
        />
      )}

      {/* Collapse 5: Totals */}
      <Collapse
        size="small"
        style={COLLAPSE_STYLE}
        styles={COLLAPSE_STYLES}
        items={[
          {
            key: 'totals',
            forceRender: true,
            label: buildCollapseLabel(<DollarOutlined />, translateX('invoice_totals')),
            children: <TotalsCard items={items} data={data} />,
          },
        ]}
      />

      {/* Collapse 6: Payments */}
      <Collapse
        size="small"
        style={COLLAPSE_STYLE}
        styles={COLLAPSE_STYLES}
        items={[
          {
            key: 'payments',
            forceRender: true,
            label: buildCollapseLabel(<CreditCardOutlined />, translateX('payments')),
            extra: paymentsHeaderExtra,
            children: (
              <PaymentsCard
                items={items}
                payments={payments}
                onAddPayment={handleAddPayment}
                onRemovePayment={handleRemovePayment}
                onActionsChange={handlePaymentsActionsChange}
                viewMode={viewMode}
                data={data}
              />
            ),
          },
        ]}
      />
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default InvoicesRecordTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildCollapseLabel(icon, text, extra = null) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', height: 40 }}>
      {icon && <span style={{ marginRight: 6 }}>{icon}</span>}
      {text}
      {extra}
    </span>
  );
}
